import React, {useState} from 'react';
import TableHeader from 'components/table/TableHeader';
import TableRow from 'components/table/TableRow';
import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch, RootState} from 'store/store';
import TableCell from 'components/table/TableCell';
import Table from 'components/table/Table';
import {UserThunks} from 'features/user/userThunks';
import {User} from 'types/user';
import {SearchType} from 'types/search';
import {setActiveTab} from 'features/user/userSlice';
import PhoneNumberDisplay from 'components/PhoneNumberDisplay';
import BlueButton from 'components/BlueButton';
import PlusIcon from 'assets/images/icons/PlusIcon';
import {useDropdownActionMenu} from 'hooks/useDropdownActionMenu';
import {useNavigate} from 'react-router-dom';
import AlertIcon from 'assets/images/icons/AlertIcon';
import DotsMenu from 'components/DotsMenu';
import {Credential} from 'types/credential';
import {JobRole} from 'types/jobRole';
import StatusBadge from 'components/StatusBadge';
import usePermissions from 'hooks/usePermissions';

const HrTable: React.FC<{
  openDrawer: () => void;
  openNewDrawer: () => void;
}> = ({openDrawer, openNewDrawer}) => {
  const users = useSelector((state: RootState) => state.user.users);
  const usersLoading = useSelector(
    (state: RootState) => state.user.status === 'loading',
  );
  const dispatch = useDispatch<AppDispatch>();
  const pagy = useSelector((state: RootState) => state.user.pagy);
  const activeTab = useSelector((state: RootState) => state.user.activeTab);
  const navigate = useNavigate();
  const {setDotsMenuIsOpen} = useDropdownActionMenu();
  const {checkPermission} = usePermissions();

  interface VisibleColumnsType {
    externalId: boolean;
    name: boolean;
    workEmail: boolean;
    personalEmail: boolean;
    phoneNumber: boolean;
    roles: boolean;
    status: boolean;
    location: boolean;
    credentials: boolean;
    lastLogin: boolean;
    supervisors: boolean;
    expDate: boolean;
  }

  const [visibleColumns, setVisibleColumns] = useState<VisibleColumnsType>({
    externalId: true,
    name: true,
    workEmail: true,
    personalEmail: true,
    phoneNumber: true,
    roles: true,
    status: true,
    location: true,
    credentials: true,
    lastLogin: true,
    supervisors: true,
    expDate: true,
  });

  const columnOptions = [
    {
      label: 'Ext ID',
      value: 'externalId',
      isVisible: visibleColumns.externalId,
    },
    {label: 'Name', value: 'name', isVisible: visibleColumns.name},
    {label: 'Status', value: 'status', isVisible: visibleColumns.status},
    {
      label: 'Certifications',
      value: 'credentials',
      isVisible: visibleColumns.credentials,
    },
    {
      label: 'Work email',
      value: 'workEmail',
      isVisible: visibleColumns.workEmail,
    },
    {
      label: 'Personal email',
      value: 'personalEmail',
      isVisible: visibleColumns.personalEmail,
    },
    {
      label: 'Phone Number',
      value: 'phoneNumber',
      isVisible: visibleColumns.phoneNumber,
    },
    {label: 'Roles', value: 'roles', isVisible: visibleColumns.roles},
    {label: 'Location', value: 'location', isVisible: visibleColumns.location},
    {
      label: 'Supervisors',
      value: 'supervisors',
      isVisible: visibleColumns.supervisors,
    },
    {
      label: 'Last Login',
      value: 'lastLogin',
      isVisible: visibleColumns.lastLogin,
    },
  ];
  const searchByOptions = [
    {label: 'Employee', value: 'name', operator: 'like'},
    {label: 'Ext ID', value: 'external_id', operator: 'like'},
    {label: 'Email', value: 'email', operator: 'like'},
  ];

  const fetchUserData = async ({
    page,
    search,
    sortField,
    sortDirection,
    rowsPerPage,
  }: {
    page: number;
    search: SearchType<{[key: string]: {operator: string; value: string}}>;
    sortField: string;
    sortDirection: string;
    rowsPerPage: number;
  }): Promise<void> => {
    if (
      activeTab === 'Active' ||
      activeTab === 'Inactive' ||
      activeTab === 'Onboarding'
    ) {
      search.where.status = {
        operator: '==',
        value: activeTab.toLowerCase(),
      };
    } else if (activeTab === 'Hidden') {
      search.where.hidden = {
        operator: '==',
        value: 'true',
      };
    } else {
      search.where.hidden = {
        operator: '==',
        value: 'false',
      };
      search.where.status = {
        operator: '==',
        value: '',
      };
    }

    await dispatch(
      UserThunks.index({page, search, sortField, sortDirection, rowsPerPage}),
    );
  };

  const handleColumnToggle = (column: string) => {
    if (column in visibleColumns) {
      setVisibleColumns(prev => ({
        ...prev,
        [column as keyof VisibleColumnsType]:
          !prev[column as keyof VisibleColumnsType],
      }));
    }
  };

  const handleOpenDrawer = (id?: string) => async () => {
    navigate(`/hr/users/${id}`);
  };
  const handleDeactivate = (userId: string) => {
    const user = new User({id: userId, status: 'inactive'});
    dispatch(UserThunks.update(user) as any);
    setDotsMenuIsOpen(null);
  };

  const handleReactivate = (userId: string) => {
    const user = new User({id: userId, status: 'active'});
    dispatch(UserThunks.update(user) as any);
    setDotsMenuIsOpen(null);
  };

  const handleUnlock = (userId: string) => {
    dispatch(UserThunks.unlock(userId) as any);
    setDotsMenuIsOpen(null);
  };

  const handleUnblock = (userId: string) => {
    dispatch(UserThunks.unblock(userId) as any);
    setDotsMenuIsOpen(null);
  };
  const handleEdit = (userId: string) => {
    dispatch(UserThunks.show(userId));
    openDrawer();
  };

  return (
    <Table
      fetchData={fetchUserData}
      pagy={pagy}
      activeTab={activeTab}
      setActiveTab={(tab: string) => dispatch(setActiveTab(tab))}
      tabs={['All', 'Onboarding', 'Active', 'Inactive', 'Hidden']}
      recordsName={'Employees'}
      searchByOptions={searchByOptions}
      blueButton={
        checkPermission('create', 'HR') ? (
          <BlueButton onClick={openNewDrawer} label={''} icon={<PlusIcon />} />
        ) : null
      }
      columns={true}
      loading={usersLoading}
      columnOptions={columnOptions}
      onColumnToggle={handleColumnToggle}>
      <thead>
        <tr>
          {visibleColumns.externalId && (
            <TableHeader label="Ext ID" sortBy="externalId" />
          )}
          {visibleColumns.name && <TableHeader label="Name" sortBy="name" />}
          {visibleColumns.status && (
            <TableHeader
              label="Status"
              sortBy={activeTab === 'All' ? 'status' : undefined}
            />
          )}
          {visibleColumns.credentials && (
            <TableHeader label="Certifications" sortBy="credentials" />
          )}
          {visibleColumns.workEmail && (
            <TableHeader label="Work email" sortBy="email" />
          )}
          {visibleColumns.personalEmail && (
            <TableHeader label="Personal email" sortBy="personalEmail" />
          )}
          {visibleColumns.phoneNumber && (
            <TableHeader label="Phone number" sortBy="phoneNumber" />
          )}
          {visibleColumns.roles && (
            <TableHeader label="Roles" sortBy="job_roles.name" />
          )}
          {visibleColumns.location && (
            <TableHeader label="Locations" sortBy="location" />
          )}
          {visibleColumns.lastLogin && (
            <TableHeader label="Last login" sortBy="lastLogin" />
          )}
          <TableHeader label="" />
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200 dark:divide-neutral-700">
        {users.length > 0 &&
          users.map((user: User) => {
            const userOptions = [];
            if (checkPermission('update', 'User')) {
              userOptions.push({
                label: 'Edit user',
                onClick: () => handleEdit(user.id),
                hoverClass: 'hover:bg-cyan-100',
              });

              if (user.status === 'Active') {
                userOptions.push({
                  label: 'Deactivate user',
                  icon: <AlertIcon />,
                  onClick: () => handleDeactivate(user.id),
                  hoverClass: 'hover:bg-red-100 hover:text-red-800',
                });
              }
              if (user.status === 'Inactive') {
                userOptions.push({
                  label: 'Activate user',
                  onClick: () => handleReactivate(user.id),
                  hoverClass: 'hover:bg-cyan-100',
                });
              }
              if (user.locked) {
                userOptions.push({
                  label: 'Unlock user',
                  onClick: () => handleUnlock(user.id),
                  hoverClass: 'hover:bg-cyan-100',
                });
              }
              if (user.blocked) {
                userOptions.push({
                  label: 'Unblock user',
                  onClick: () => handleUnblock(user.id),
                  hoverClass: 'hover:bg-cyan-100',
                });
              }
            }

            return (
              <TableRow key={user.id}>
                {visibleColumns.externalId && (
                  <TableCell>{user.externalId}</TableCell>
                )}
                {visibleColumns.name && (
                  <TableCell>
                    <div className=" relative group">
                      <div className="w-full flex items-center gap-x-3">
                        <div className="flex-grow">
                          <span
                            className="text-sm  cursor-pointer font-medium dark:text-neutral-200 underline text-cyan-600"
                            onClick={handleOpenDrawer(user.id)}>
                            {user.firstName} {user.lastName}
                          </span>
                        </div>
                      </div>
                    </div>
                  </TableCell>
                )}
                {visibleColumns.status && (
                  <TableCell>
                    <StatusBadge
                      status={user.status}
                      locked={user.locked}
                      blocked={user.blocked}
                    />
                  </TableCell>
                )}
                {visibleColumns.credentials && (
                  <TableCell>
                    {user.credentials
                      .map((credential: Credential) => credential.name)
                      .join(', ')}
                  </TableCell>
                )}
                {visibleColumns.workEmail && (
                  <TableCell>{user.email}</TableCell>
                )}
                {visibleColumns.personalEmail && (
                  <TableCell>{user.personalEmail}</TableCell>
                )}
                {visibleColumns.phoneNumber && (
                  <TableCell>
                    <PhoneNumberDisplay value={user.phoneNumber || ''} />
                  </TableCell>
                )}
                {visibleColumns.roles && (
                  <TableCell className="whitespace-nowrap overflow-hidden text-ellipsis">
                    <div
                      style={{maxWidth: '150px'}}
                      className="whitespace-nowrap overflow-hidden text-ellipsis">
                      {user.jobRoles
                        .map((role: JobRole) => role.name)
                        .join(', ')}
                    </div>
                  </TableCell>
                )}

                {visibleColumns.location && (
                  <TableCell>
                    {user.locations
                      .map((location: any) => location.name)
                      .join(', ')}
                  </TableCell>
                )}
                {visibleColumns.lastLogin && (
                  <TableCell>{user.lastLogin}</TableCell>
                )}
                <TableCell>
                  <DotsMenu options={userOptions} />
                </TableCell>
              </TableRow>
            );
          })}
      </tbody>
    </Table>
  );
};

export default HrTable;
